import { useState, useMemo, useEffect } from 'react'

import { SORT_DIRECTION, SortDirection } from '../../DomainPresentation/utils'
import { GenericPlatformEntity } from '../models'
import { filterDuplicateNumbers } from '../../../utils/array'

/*
  Hooks provides all necessary state and logic for IdsList component
  Intended to be used in GenericCheckboxModal or similar components
*/

export const useIdsList = (
  accounts: GenericPlatformEntity[],
  selectedOnServer?: number,
  isFromSearch?: boolean,
  isPaginationEnabled?: boolean
) => {
  const [filterBy, setFilter] = useState<string>('')
  const [sort, setSort] = useState<SortDirection>(SORT_DIRECTION.ASC)
  const [selectedOptions, setSelectedOptions] = useState<number[]>([])
  // this is accumulator that returns how much of the selected options were extracted from 'selected' key in the response and selected by clicking
  // needed to correctly display the number of selected options in the modal includoing those that selected on other pages
  const [fromServerSelected, setFromServerSelected] = useState<number[]>([])

  const selectedCount = (selectedOnServer ?? 0) + selectedOptions.length - fromServerSelected.length

  const setFilterBy = (value: string) => {
    setFilter(value.toLocaleLowerCase())
  }

  const handleSortChange = () => {
    !isPaginationEnabled &&
      setSort((state) => (state === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC))
  }

  const handleBunchSelect = (ids: number[]) => {
    const uniqueIds = filterDuplicateNumbers([...selectedOptions, ...ids])
    setSelectedOptions(uniqueIds)
  }

  const getSelectedFromResponse = (entities: GenericPlatformEntity[]): number[] => {
    const selected: number[] = []
    entities.forEach((entity) => {
      if (entity.selected && !fromServerSelected.some((acc) => acc === entity.id))
        selected.push(entity.id)
    })
    setFromServerSelected((fromServerSelected) =>
      filterDuplicateNumbers([...selected, ...fromServerSelected])
    )
    return selected
  }

  const handleCheckboxClicked = (id: number) => {
    if (selectedOptions.includes(id)) {
      setSelectedOptions((state) => state.filter((selectedId) => id !== selectedId))
    } else {
      setSelectedOptions((state) => [...state, id])
    }
  }

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedOptions([])
    } else {
      setSelectedOptions(
        Array.from(
          new Set([
            ...selectedOptions,
            ...accounts.map((account: GenericPlatformEntity) => account.id),
          ])
        )
      )
    }
  }

  const cleanSelection = () => {
    setSelectedOptions([])
  }

  useEffect(() => {
    if (!isFromSearch) {
      // if response come from search - we dont count them as selected
      // otherwise our count won't be correct
      handleBunchSelect(getSelectedFromResponse(accounts))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts])

  const isAllSelected = selectedOptions.length > 0 && accounts.length === selectedOptions.length

  const filteredOptions = useMemo(() => {
    let filteredSortedAccounts = accounts
    if (filterBy) {
      filteredSortedAccounts = filteredSortedAccounts.filter(
        (account: GenericPlatformEntity) =>
          account.adAccountId?.toString().includes(filterBy) ||
          account.adAccountID?.toString().includes(filterBy) ||
          account.campaignId?.includes(filterBy) ||
          account.name.toLocaleLowerCase().includes(filterBy)
      )
    }
    if (!isPaginationEnabled) {
      if (sort === SORT_DIRECTION.ASC) {
        filteredSortedAccounts.sort((a: GenericPlatformEntity, b: GenericPlatformEntity) =>
          a.name.localeCompare(b.name)
        )
      } else {
        filteredSortedAccounts.sort((a: GenericPlatformEntity, b: GenericPlatformEntity) =>
          b.name.localeCompare(a.name)
        )
      }
    }
    return filteredSortedAccounts
  }, [filterBy, sort, accounts, isPaginationEnabled])

  return {
    filteredOptions,
    handleCheckboxClicked,
    handleSelectAll,
    selectedOptions,
    selectedCount,
    setFilterBy,
    filterBy,
    isAllSelected,
    cleanSelection,
    handleBunchSelect,
    handleSortChange,
    sortOrder: sort,
  }
}
